import React, { useMemo } from 'react';

import { useIntl } from 'react-intl';
import { useFragment } from 'react-relay';
import { graphql } from 'relay-runtime';

import Button from '@atlaskit/button/new';
import Tooltip from '@atlaskit/tooltip';
import type { ActionKeys } from '@atlassian/conversation-assistant-message-actions-api/constants';
import type {
	ActionCallbacks,
	ActionRendererKeyToPayload,
	ActionWith,
} from '@atlassian/conversation-assistant-message-actions-api/types';

import type { projectStatusUpdateCardFooter_AtlassianConversationAssistantMessageActions_CardFooterWithTooltip$key } from './__generated__/projectStatusUpdateCardFooter_AtlassianConversationAssistantMessageActions_CardFooterWithTooltip.graphql';
import { messages } from './messages';

export type FooterWithTooltipCTAProps = {
	action: ActionWith<ActionRendererKeyToPayload[typeof ActionKeys.CreateProjectProgressUpdate]>;
	isDisabled: boolean;
	isSubmitting: boolean;
	currentUserId: string | null | undefined;
	fragmentKey?: projectStatusUpdateCardFooter_AtlassianConversationAssistantMessageActions_CardFooterWithTooltip$key | null;
} & Pick<ActionCallbacks, 'onConfirm' | 'onCancel'>;

export type FooterCTAProps = {
	action: ActionWith<ActionRendererKeyToPayload[typeof ActionKeys.CreateProjectProgressUpdate]>;
	isDisabled: boolean;
	isSubmitting: boolean;
} & Pick<ActionCallbacks, 'onConfirm' | 'onCancel'>;

export const CardFooterWithTooltip = ({
	action,
	onConfirm,
	onCancel,
	isDisabled,
	isSubmitting,
	currentUserId,
	fragmentKey,
}: FooterWithTooltipCTAProps): React.JSX.Element => {
	const intl = useIntl();

	const projectDetails = useFragment(
		graphql`
			fragment projectStatusUpdateCardFooter_AtlassianConversationAssistantMessageActions_CardFooterWithTooltip on TownsquareProject {
				members {
					edges {
						node {
							id
						}
					}
				}
			}
		`,
		fragmentKey,
	);

	const projectMembers = projectDetails?.members?.edges?.map((node) => node?.node);

	// Check if current user is a member of the project
	const isUserAMember = useMemo(() => {
		if (!currentUserId || !projectMembers || projectMembers.length === 0) {
			return false; // Disallow action if we can't determine membership
		}
		return projectMembers.some((member) => member?.id === currentUserId);
	}, [currentUserId, projectMembers]);

	const isPostButtonDisabled = isDisabled || !isUserAMember;

	const cancelButton = (
		<Button appearance="subtle" onClick={() => onCancel(action)} isDisabled={isDisabled}>
			{intl.formatMessage(messages.ctaCancel)}
		</Button>
	);

	const postButton = (
		<Button
			appearance="primary"
			onClick={() => onConfirm(action)}
			isDisabled={isPostButtonDisabled}
			isLoading={isSubmitting}
		>
			{intl.formatMessage(messages.ctaPostUpdate)}
		</Button>
	);

	if (isUserAMember) {
		return (
			<>
				{cancelButton}
				{postButton}
			</>
		);
	}

	return (
		<>
			{cancelButton}
			<Tooltip content={intl.formatMessage(messages.ctaForbiddenPostUpdate)} position="top">
				{(tooltipProps) => <div {...tooltipProps}>{postButton}</div>}
			</Tooltip>
		</>
	);
};

export const CardFooter = ({
	action,
	onConfirm,
	onCancel,
	isDisabled,
	isSubmitting,
}: FooterCTAProps): React.JSX.Element => {
	const intl = useIntl();

	return (
		<>
			<Button appearance="subtle" onClick={() => onCancel(action)} isDisabled={isDisabled}>
				{intl.formatMessage(messages.ctaCancel)}
			</Button>
			<Button
				appearance="primary"
				onClick={() => onConfirm(action)}
				isDisabled={isDisabled}
				isLoading={isSubmitting}
			>
				{intl.formatMessage(messages.ctaPostUpdate)}
			</Button>
		</>
	);
};
